import { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import { getDashboard } from '../../services/api';
import './Dashboard.css';

const ACTIVITY_COLORS = {
  UPLOAD: { color: '#f59e0b', bg: '#fef3c7', label: 'Upload' },
  DNC: { color: '#dc2626', bg: '#fee2e2', label: 'DNC' },
  BOUNCE: { color: '#ea580c', bg: '#ffedd5', label: 'Bounce' },
  EDIT: { color: '#1d4ed8', bg: '#dbeafe', label: 'Edit' },
};

const PALETTE = [
  '#676aefff', '#22c55e', '#e530c7ff', '#ef4444', '#68c4effb',
  '#a855f7', '#eab308', '#f97316', '#ec4899', '#14b8a6',
];

const LOCATION_PALETTE = [
  '#0ea5e9', '#22c55e', '#a855f7', '#fb923c', '#e11d48',
  '#0891b2', '#15803d', '#7c3aed', '#c2410c', '#be185d',
];

function formatTime(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const now = new Date();
  const diffMin = Math.floor((now - d) / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);  
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return d.toLocaleDateString();
}

/* ── Custom bar label shown to the right of each bar ── */
function BarValueLabel({ x, y, width, value }) {
  return (
    <text x={x + width + 6} y={y + 11} fontSize={11} fill="#64748b" dominantBaseline="middle">
      {value?.toLocaleString()}
    </text>
  );
}

/* ── Custom label for pie chart slices (external with connector lines) ── */
function PieLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent, name, fill }) {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const sin = Math.sin(-midAngle * RADIAN);
  const cos = Math.cos(-midAngle * RADIAN);
  const lx = cx + (outerRadius + 14) * cos;
  const ly = cy + (outerRadius + 14) * sin;
  const ex = cx + (outerRadius + 34) * cos;
  const ey = cy + (outerRadius + 34) * sin;

  return (
    <g>
      <path d={`M${x},${y} L${lx},${ly} L${ex},${ey}`} fill="none" stroke={fill} strokeWidth={1} />
      <text x={ex + (cos >= 0 ? 4 : -4)} y={ey} textAnchor={cos >= 0 ? 'start' : 'end'} dominantBaseline="middle" fill={fill} fontSize={13} fontWeight={500}>
        {name} ({(percent * 100).toFixed(0)}%)
      </text>
    </g>
  );
}

/* ── Custom donut legend matching reference image ── */
function DonutLegend({ data, palette }) {
  return (
    <div className="ds-donut-legend">
      {data.map((d, i) => (
        <div className="ds-donut-legend-item" key={i}>
          <span className="ds-donut-dot" style={{ background: palette[i % palette.length] }} />
          <span className="ds-donut-label">{d.name}</span>
          <span className="ds-donut-count">{d.value.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

function StatCard({ label, value, icon, color, bg, onClick }) {
  return (
    <div className={`ds-stat-card${onClick ? ' ds-stat-card--clickable' : ''}`} onClick={onClick}>
      <div className="ds-stat-icon" style={{ background: bg, color }}>
        {icon}
      </div>
      <div className="ds-stat-body">
        <span className="ds-stat-label">{label}</span>
        <span className="ds-stat-value">{value?.toLocaleString() ?? '—'}</span>
      </div>
    </div>
  );
}

export default function Dashboard({ onModuleChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getDashboard();
      setData(res.data);
    } catch (e) {
      setError(e.message || 'Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading) {
    return (
      <div className="ds-loader-wrap">
        <div className="ds-spinner" />
        <span>Loading dashboard…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ds-error-banner">
        {error}
        <button onClick={fetchStats}>Retry</button>
      </div>
    );
  }

  const go = (module, extra) => onModuleChange && onModuleChange(module, extra);

  const stats = [
    {
      label: 'Total Companies', value: data.totalCompanies,
      color: '#1d4ed8', bg: '#dbeafe',
      onClick: () => go('companies'),
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" /></svg>,
    },
    {
      label: 'Total Contacts', value: data.totalContacts,
      color: '#0891b2', bg: '#cffafe',
      onClick: () => go('contacts'),
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
    },
    {
      label: 'Active Contacts', value: data.activeContacts,
      color: '#10b981', bg: '#d1fae5',
      onClick: () => go('contacts'),
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>,
    },
    {
      label: 'Do Not Contact', value: data.dncCount,
      color: '#dc2626', bg: '#fee2e2',
      onClick: () => go('dncBounce', { tab: 'DNC' }),
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></svg>,
    },
    {
      label: 'Bounce Count', value: data.bounceCount,
      color: '#ea580c', bg: '#ffedd5',
      onClick: () => go('dncBounce', { tab: 'BOUNCE' }),
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 .49-3.51" /></svg>,
    },
    {
      label: 'Total Purposes', value: data.totalPurposes,
      color: '#7c3aed', bg: '#ede9fe',
      onClick: () => go('contacts'),
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>,
    },
    {
      label: 'Activities Logged', value: data.totalActivities,
      color: '#0d9488', bg: '#ccfbf1',
      onClick: () => go('contacts'),
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>,
    },
  ];

  /* Prepare chart data */
  const industryData = (data.byIndustry ?? []).map((d, i) => ({
    name: d.label, value: d.count, fill: PALETTE[i % PALETTE.length],
  }));

  const locationData = (data.byLocation ?? []).map((d, i) => ({
    name: d.label, value: d.count, fill: LOCATION_PALETTE[i % LOCATION_PALETTE.length],
  }));

  /* RadialBarChart needs each item to carry its own fill */
  const purposeData = (data.byPurpose ?? []).map((d, i) => ({
    name: d.label, value: d.count, fill: PALETTE[i % PALETTE.length],
  }));
  const totalPurposeValue = purposeData.reduce((sum, item) => sum + item.value, 0) || 1;

  return (
    <div className="ds-dashboard">

      {/* ── Stats ─────────────────────────────────────────────────────── */}
      <div className="ds-stats-grid">
        {stats.map((s, i) => <StatCard key={i} {...s} />)}
      </div>

      {/* ── Bar charts row ────────────────────────────────────────────── */}
      <div className="ds-charts-row">

        {/* Industry – pie chart with external labels */}
        <div className="ds-chart-card">
          <div className="ds-card-header">
            <h3>Contacts by Industry</h3>
            <span className="ds-card-badge">Top {industryData.length}</span>
          </div>
          {industryData.length === 0 ? (
            <div className="ds-chart-empty">No data yet</div>
          ) : (
            <div className="ds-pie-wrap">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={industryData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius="75%"
                    labelLine
                    label={(props) => <PieLabel {...props} />}
                    isAnimationActive
                  >
                    {industryData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Pie>
                  <Tooltip formatter={(v, name) => [v.toLocaleString(), name]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Location – vertical bars, teal/blue palette */}
        <div className="ds-chart-card">
          <div className="ds-card-header">
            <h3>Contacts by Country</h3>
            <span className="ds-card-badge">Top {locationData.length}</span>
          </div>
          {locationData.length === 0 ? (
            <div className="ds-chart-empty">No data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={locationData} margin={{ top: 4, right: 50, left: 0, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  angle={-40}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <Tooltip
                  cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                  formatter={(v, name) => [v.toLocaleString(), name]}
                />
                <Bar dataKey="value" radius={[6, 6, 0, 0]} label={<BarValueLabel />} isAnimationActive>
                  {locationData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ── Radial chart + Activity ───────────────────────────────────── */}
      <div className="ds-bottom-row">

        {/* Purpose – donut chart */}
        <div className="ds-chart-card">
          <div className="ds-card-header">
            <h3>Contacts by Purpose</h3>
          </div>
          {purposeData.length === 0 ? (
            <div className="ds-chart-empty">No data yet</div>
          ) : (
            <div className="ds-donut-wrap">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={purposeData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius="55%"
                    outerRadius="85%"
                    paddingAngle={2}
                    cornerRadius={4}
                    isAnimationActive
                  >
                    {purposeData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Pie>
                  <Tooltip
                    formatter={(v, name) => [
                      `${((v / totalPurposeValue) * 100).toFixed(1)}%`,
                      name
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <DonutLegend data={purposeData} palette={PALETTE} />
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="ds-activity-card">
          <div className="ds-card-header">
            <h3>Recent Activity</h3>
          </div>
          <div className="ds-activity-list">
            {(data.recentActivity ?? []).length === 0 && (
              <div className="ds-chart-empty">No recent activity</div>
            )}
            {(data.recentActivity ?? []).map((a, i) => {
              const tc = ACTIVITY_COLORS[a.type] ?? ACTIVITY_COLORS.EDIT;
              return (
                <div className="ds-activity-item" key={i}>
                  <div className="ds-activity-dot" style={{ background: tc.bg, color: tc.color }}>
                    {a.performedBy?.[0]?.toUpperCase() ?? '?'}
                  </div>
                  <div className="ds-activity-body">
                    <span className="ds-activity-user">{a.performedBy}</span>
                    <span className="ds-activity-desc">{a.description}</span>
                  </div>
                  <div className="ds-activity-meta">
                    <span className="ds-activity-badge" style={{ background: tc.bg, color: tc.color }}>
                      {tc.label}
                    </span>
                    <span className="ds-activity-time">{formatTime(a.time)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
